import { initializeApp, getApps, getApp } from 'firebase/app';
import {
  initializeAuth, getAuth, signInAnonymously, onAuthStateChanged, connectAuthEmulator,
  browserPopupRedirectResolver, indexedDBLocalPersistence, browserLocalPersistence, browserSessionPersistence,
  type Auth, type PopupRedirectResolver, type User,
} from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { isMetaInAppBrowser } from './lib/in-app-browser';

const config = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};
const databaseId = import.meta.env.VITE_FIREBASE_DATABASE_ID as string | undefined;

/**
 * getAuth()'s own popup/redirect resolver, minus one thing. On a phone or in
 * Safari, Firebase loads Google's gapi script and the auth iframe (~135 KB,
 * three requests in a row) before it will even say who is signed in, so that
 * a later popup opens quickly enough to keep the tap's permission. Every
 * Firestore read at boot waited behind it, on every visit, for a button only
 * the Feed has (Lighthouse, 2026-10-08). Everything else is the stock resolver
 * — a Google redirect coming back still finishes at startup, which auth cannot
 * do without one — and the Feed warms it instead (warmGoogleSave).
 */
const StockResolver = browserPopupRedirectResolver as unknown as new () => object;
class LazyPopupRedirectResolver extends StockResolver {}
Object.defineProperty(LazyPopupRedirectResolver.prototype, '_shouldInitProactively', { get: () => false });

export const app = getApps().length ? getApp() : initializeApp(config);

/**
 * Where the sign-in is kept: getAuth's own order (IndexedDB first), except in
 * Facebook's and Instagram's in-app browsers, which keep it in localStorage.
 *
 * 2026-10-14: a visitor from a Meta ad switched away from Facebook mid-Day 1
 * and came back a minute later to a reload. localStorage had survived (same
 * PostHog id; the cached road still said the intro was seen) but the anonymous
 * sign-in, which auth had put in IndexedDB, had not: boot made a second
 * anonymous user and the intro started again. WebKit's IndexedDB is the
 * fragile one inside iOS apps: its connection dies with WebKit's networking
 * process while the app is in the background and is not recovered without a
 * reload (WebKit bugs 273827, 277615 → 309386), and auth polls IndexedDB every
 * 800 ms, so a record that reads back missing signs the open page out as if
 * another tab had.
 *
 * Kept in localStorage, the sign-in lives and dies with the road cached beside
 * it (state.ts) and the PostHog id: a browser that keeps one keeps all three,
 * and one that clears them clears all three, which is a clean new person
 * rather than a person split in two. Auth still looks for a user in every
 * persistence listed, so somebody already signed in through IndexedDB is moved
 * across on their next load, not signed out.
 *
 * Only there, not everywhere: Safari and Chrome have not shown the problem, and
 * changing where every existing user's sign-in lives is a migration nobody
 * needs. profile-sync's adopt rule is the net under both.
 */
const persistence = isMetaInAppBrowser()
  ? [browserLocalPersistence, indexedDBLocalPersistence, browserSessionPersistence]
  : [indexedDBLocalPersistence, browserLocalPersistence, browserSessionPersistence]; // getAuth's own

function makeAuth(): Auth {
  try {
    return initializeAuth(app, {
      persistence,
      popupRedirectResolver: LazyPopupRedirectResolver as unknown as PopupRedirectResolver,
    });
  } catch {
    return getAuth(app); // already initialised: HMR has re-run this module
  }
}

export const auth = makeAuth();
// DEV only: VITE_FIREBASE_AUTH_EMULATOR=http://127.0.0.1:9099 signs in against the
// local Auth emulator, where a save's email link can be read back from its REST
// api instead of a mailbox, and a handoff's custom token needs no signing key.
const authEmulator = import.meta.env.DEV ? (import.meta.env.VITE_FIREBASE_AUTH_EMULATOR as string | undefined) : undefined;
if (authEmulator) connectAuthEmulator(auth, authEmulator, { disableWarnings: true });
export const db = databaseId ? getFirestore(app, databaseId) : getFirestore(app);

let signingIn: Promise<User> | null = null;

/**
 * Identity is anonymous from the first tap (10 §12). The soft save (slice 2)
 * links this identity to an email in place, so nothing moves.
 *
 * Shared while in flight: a new person gets a screen before boot has signed
 * them in, so a screen and boot can ask at the same moment, and two calls that
 * each find nobody would make two anonymous users.
 */
export function ensureUser(): Promise<User> {
  if (auth.currentUser) return Promise.resolve(auth.currentUser);
  signingIn ??= new Promise<User>((resolve, reject) => {
    const stop = onAuthStateChanged(auth, (user) => {
      if (user) { stop(); resolve(user); return; }
      signInAnonymously(auth).catch((err) => { stop(); reject(err); });
    }, reject);
  }).finally(() => { signingIn = null; });
  return signingIn;
}

export async function idToken(): Promise<string> {
  const user = await ensureUser();
  return user.getIdToken();
}
